import { CouponApplyResult } from 'src/Components/ScheduleSummary'
import { useAppDispatch } from 'src/Hooks/redux'
import { useOrderBudgetSync } from 'src/Hooks/useOrderBudgetSync'
import { useThresholdCoupon } from 'src/Hooks/useThresholdCoupon'
import { useThresholdCouponSummary } from 'src/Hooks/useThresholdCouponSummary'
import { useGetTikTokCampaignBudgetRangeQuery } from 'src/Redux/artistPaymentSettingsApi'
import {
  Order,
  OrderCheckoutInfoResponse,
  OrderItemType,
  OrderStatus,
} from 'src/Redux/artistOrderApi'
import { getAudienceTiktok } from 'src/Redux/schedule-process/audienceTiktokSlice/api-actions'
import {
  CampaignPlatformTypeEnum,
  TikTokCampaignPricingModelEnum,
} from 'src/Types/campaigns'

type UseTikTokThresholdCouponParams = {
  campaignId: number
  budget: number
  appliedCoupon: string
  isOrderFullyCovered: boolean
  isPaymentSucceeded: boolean
  pricingModel?: TikTokCampaignPricingModelEnum | null
  currentOrder?: Order | null
  checkoutInfoData?: OrderCheckoutInfoResponse | null
  formatAmount: (value: number) => string
  onApplyPromocode: (
    couponCode: string,
  ) => Promise<CouponApplyResult | undefined>
  onRemoveCoupon: () => void
  updateOrderItem: Parameters<typeof useOrderBudgetSync>[0]['updateOrderItem']
  triggerCheckoutInfo: Parameters<
    typeof useOrderBudgetSync
  >[0]['triggerCheckoutInfo']
}

export const useTikTokThresholdCoupon = ({
  campaignId,
  budget,
  appliedCoupon,
  isOrderFullyCovered,
  isPaymentSucceeded,
  pricingModel,
  currentOrder,
  checkoutInfoData,
  formatAmount,
  onApplyPromocode,
  onRemoveCoupon,
  updateOrderItem,
  triggerCheckoutInfo,
}: UseTikTokThresholdCouponParams): ReturnType<
  typeof useThresholdCouponSummary
> & { isSyncing: boolean } => {
  const dispatch = useAppDispatch()
  const isOrderMutable = currentOrder?.status !== OrderStatus.Checkout
  const { data: budgetRange } = useGetTikTokCampaignBudgetRangeQuery(
    pricingModel ?? null,
  )

  const applyBudget = async (value: number): Promise<void> => {
    if (!pricingModel) {
      return
    }
    await dispatch(getAudienceTiktok({ amount: value, pricingModel })).unwrap()
  }

  const isSyncing = useOrderBudgetSync({
    enabled: Boolean(checkoutInfoData) && !isPaymentSucceeded,
    orderId: currentOrder?.id,
    campaignItem: currentOrder?.items.find(
      (item) =>
        item.type === OrderItemType.TiktokCampaign &&
        item.sourceId === campaignId,
    ),
    coupons: appliedCoupon ? [appliedCoupon] : [],
    budget,
    updateOrderItem,
    triggerCheckoutInfo,
    isOrderMutable,
  })

  const threshold = useThresholdCoupon({
    checkoutInfo: checkoutInfoData,
    isManualMatching: false,
    budgetRange: {
      min: budgetRange?.campaignMinBudget,
      max: budgetRange?.campaignMaxBudget,
    },
    appliedCoupon,
    budget,
    isOrderFullyCovered,
    isOrderMutable,
    onApplyPromocode,
    onRemoveCoupon,
    applyBudget,
  })

  const isHidden = isOrderFullyCovered
  const totalDiscount = checkoutInfoData?.breakdown.totalDiscount ?? 0

  const summary = useThresholdCouponSummary({
    threshold,
    isHidden,
    campaignId,
    totalDiscount,
    formatAmount,
    onApplyPromocode,
    platformName: CampaignPlatformTypeEnum.TIKTOK,
  })

  return { ...summary, isSyncing }
}
